import { z } from 'zod'

/**
 * 관리자 권한 나누기 (C52, 2026-10-29). 설계: docs/design-access-grants.md
 *
 * - 최고 관리자(owner): 지금까지의 관리자 전부(ADMIN_EMAILS · Clerk role=admin · admin_users). 모든 관리자 API
 * - 배정 운영자(operator): 관리자가 아니면서 살아 있는 배정(access_grants)이 있는 사람. 배정된 견적만, 정해진 몇 라우트만
 * - 제조 파트너: 관리자 API 와 무관하다. 제조 연동 계정(manufacturing_factory_accounts)이 자기 공장 화면만 연다
 */
export const ADMIN_ACCESS_LEVELS = ['owner', 'operator'] as const
export type AdminAccessLevel = (typeof ADMIN_ACCESS_LEVELS)[number]

/** 배정할 수 있는 대상. 지금은 견적 요청 하나씩 (공장 단위 운영자 화면이 생기면 여기에 더한다) */
export const ACCESS_GRANT_SCOPES = ['quote'] as const
export type AccessGrantScope = (typeof ACCESS_GRANT_SCOPES)[number]

/** POST /admin/access-grants — 이 이메일(가입·인증된 Clerk 계정)에 이 견적을 배정 */
export const accessGrantCreateSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(120),
  scopeType: z.enum(ACCESS_GRANT_SCOPES),
  scopeId: z.string().trim().min(1).max(64),
})
export type AccessGrantCreate = z.infer<typeof accessGrantCreateSchema>

/** 관리자 화면의 배정 한 줄. 회수한 줄도 기록으로 보인다 */
export interface AccessGrantView {
  id: string
  userId: string
  email: string
  scopeType: AccessGrantScope
  scopeId: string
  grantedBy: string
  createdAt: string
  revokedAt: string | null
  revokedBy: string | null
}

/**
 * GET /admin/me 의 access (C52 부터, 더하기만 한 칸). 옛 서버는 이 칸이 없다 → 관리자 화면은 최고 관리자로 보고
 * 권한 관리 화면을 숨긴다. grantsEnabled 는 권한 관리 화면을 보일지(서버 코드 + 관리자 설정 두 겹)
 */
export interface AdminAccessView {
  level: AdminAccessLevel
  grantsEnabled: boolean
}

/**
 * 관리자 설정 accessGrants (C52). 켜야 배정 운영자가 관리자 API 에 들어온다. 꺼져 있으면 배정이 있어도 지금처럼
 * 'Admin only' 403 이고, 최고 관리자는 켜든 끄든 그대로다. 끄면 모든 운영자 접근이 한 번에 막힌다(배정 기록은 남는다)
 */
export const accessGrantsSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type AccessGrantsSettings = z.infer<typeof accessGrantsSettingsSchema>
export const DEFAULT_ACCESS_GRANTS_SETTINGS: AccessGrantsSettings = { enabled: false }
